import React, { useState, useEffect, useRef } from 'react';
import { X, Filter, ChevronDown } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getAllCategories } from '../../services/categoryService';
import logger from '../../utils/logger';
import './SimpleProductFilter.css';

interface CustomField {
  name: string;
  label?: string;
  value?: string;
  type: string;
  read_only?: boolean;
  options?: string[];
}

interface SimpleProductFilterProps {
  onApplyFilter: (filterData: any) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const SimpleProductFilter: React.FC<SimpleProductFilterProps> = ({
  onApplyFilter,
  hasActiveFilters,
  onClearFilters
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    category: '',
    customField: '',
    customFieldOperator: '',
    customFieldValue: ''
  });

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get custom field definitions
  const customFieldDefs: CustomField[] = useSelector((state: any) =>
    state?.rootReducers?.userData?.userDetails?.userPreference?.productForm || []
  );

  // Get available custom fields (non-readonly ones)
  const availableCustomFields: CustomField[] = customFieldDefs.filter((field: CustomField) => 
    !field.read_only && field.name && field.type
  );

  // Get current selected custom field
  const selectedCustomField = availableCustomFields.find(field => 
    (field.value || field.name) === filters.customField
  );

  // Get operator options based on field type
  const getOperatorOptions = () => {
    if (!selectedCustomField) return [];
    
    switch (selectedCustomField.type) {
      case 'text':
      case 'name':
      case 'email':
        return [
          { value: 'contain', label: 'Contains' },
          { value: 'eq', label: 'Equals' },
          { value: 'ne', label: 'Not equals' }
        ];
      case 'selection':
        return [
          { value: 'eq', label: 'Equals' },
          { value: 'ne', label: 'Not equals' }
        ];
      case 'number':
      case 'amount':
        return [
          { value: 'eq', label: 'Equals' },
          { value: 'gt', label: 'Greater than' },
          { value: 'lt', label: 'Less than' },
          { value: 'gte', label: 'Greater than or equal' },
          { value: 'lte', label: 'Less than or equal' }
        ];
      default:
        return [
          { value: 'contain', label: 'Contains' },
          { value: 'eq', label: 'Equals' }
        ];
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getAllCategories({});
      if (response?.data?.data) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      
      // Reset dependent fields when custom field changes
      if (field === 'customField') {
        newFilters.customFieldOperator = '';
        newFilters.customFieldValue = '';
      }
      
      // Reset value when operator changes
      if (field === 'customFieldOperator') {
        newFilters.customFieldValue = '';
      }
      
      return newFilters;
    });
  };

  const applyFilters = () => {
    const filterData: any = {
      page: 1,
      perPage: 12,
      isAscending: false
    };

    // Add category filter
    if (filters.category) {
      filterData.category = filters.category;
    }

    // Add custom field filter
    if (filters.customField && filters.customFieldOperator && filters.customFieldValue) {
      const customFieldFilters = [{
        fieldName: filters.customField, // Send field value directly (e.g., "possession_tarr")
        single: { 
          operator: filters.customFieldOperator, 
          value: filters.customFieldValue 
        }
      }];
      
      filterData.customFieldFilter = customFieldFilters;
    }

    logger.log('Applying simple product filter:', filterData);
    onApplyFilter(filterData);
    setIsOpen(false);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      customField: '',
      customFieldOperator: '',
      customFieldValue: ''
    });
    onClearFilters();
    setIsOpen(false);
  };

  const hasFilters = filters.category || 
                    (filters.customField && filters.customFieldOperator && filters.customFieldValue);

  return (
    <div className="simple-product-filter" ref={dropdownRef}>
      <div className="filter-buttons-group">
        <button
          className={`filter-toggle-btn ${hasActiveFilters ? 'active' : ''}`}
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          <Filter size={16} />
          {hasActiveFilters ? 'Filters Applied' : 'Filter'}
          <ChevronDown size={14} className={`chevron ${isOpen ? 'open' : ''}`} />
        </button>

        {hasActiveFilters && (
          <button
            className="clear-filters-btn"
            onClick={clearFilters}
            type="button"
          >
            <X size={14} />
            Clear
          </button>
        )}
      </div>

      {isOpen && (
        <div className="filter-dropdown">
          <div className="filter-header">
            <h4>Filter Products</h4>
            <button
              className="close-btn"
              onClick={() => setIsOpen(false)}
              type="button"
            >
              <X size={16} />
            </button>
          </div>

          <div className="filter-content">
            {/* Category Filter */}
            <div className="filter-group">
              <label>Category</label>
              <select
                value={filters.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="filter-select"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat._id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Fields */}
            {availableCustomFields.length > 0 && (
              <div className="filter-group">
                <label>Custom Field</label>
                <select
                  value={filters.customField}
                  onChange={(e) => handleInputChange('customField', e.target.value)}
                  className="filter-select"
                >
                  <option value="">Select Field</option>
                  {availableCustomFields.map((field: CustomField) => (
                    <option key={field.value || field.name} value={field.value || field.name}>
                      {field.label || field.name}
                    </option>
                  ))}
                </select>
                
                {/* Operator Selection */}
                {filters.customField && (
                  <div style={{ marginTop: '8px' }}>
                    <label>Operator</label>
                    <select
                      value={filters.customFieldOperator}
                      onChange={(e) => handleInputChange('customFieldOperator', e.target.value)}
                      className="filter-select"
                    >
                      <option value="">Select Operator</option>
                      {getOperatorOptions().map(op => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Value Input/Selection */}
                {filters.customField && filters.customFieldOperator && (
                  <div style={{ marginTop: '8px' }}>
                    <label>Value</label>
                    {selectedCustomField?.type === 'selection' && selectedCustomField?.options ? (
                      <select
                        value={filters.customFieldValue}
                        onChange={(e) => handleInputChange('customFieldValue', e.target.value)}
                        className="filter-select"
                      >
                        <option value="">Select Value</option>
                        {selectedCustomField.options.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={selectedCustomField?.type === 'number' || selectedCustomField?.type === 'amount' ? 'number' : 'text'}
                        placeholder="Enter value..."
                        value={filters.customFieldValue}
                        onChange={(e) => handleInputChange('customFieldValue', e.target.value)}
                        className="filter-input"
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="filter-actions">
              <button
                className="clear-btn"
                onClick={clearFilters}
                type="button"
              >
                Clear
              </button>
              <button
                className="apply-btn"
                onClick={applyFilters}
                type="button"
                disabled={!hasFilters}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleProductFilter;